import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { NgxSpinnerService } from "ngx-spinner";
import Swal from "sweetalert2";
import { HttpHelpService } from "../../service/http-help.service";

@Component({
	selector: "app-admin-custom-payments",
	templateUrl: "./admin-custom-payments.component.html",
	styleUrls: ["./admin-custom-payments.component.scss"],
})
export class AdminCustomPaymentsComponent implements OnInit {
	curentLang: string = localStorage.getItem("curentLang") || "en";

	customPaymentForm: FormGroup;
	cpMerchants: any[] = [];
	cpMerchantsLoaded = false;
	cpRows: any[] = [];
	cpTotal = 0;
	cpPage = 1;
	cpLimit = 20;
	cpFilterMerchantId: number | null = null;
	cpFilterStatus: "" | "pending" | "paid" | "failed" = "";
	isCustomPaymentSubmitting = false;
	lastPaymentUrl: string | null = null;

	constructor(
		private service: HttpHelpService,
		private spinner: NgxSpinnerService,
		private modalService: NgbModal,
		private router: Router
	) {
		this.customPaymentForm = new FormGroup({
			merchant_id: new FormControl(null, [Validators.required, Validators.min(1)]),
			title: new FormControl("", [Validators.required, Validators.maxLength(255)]),
			description: new FormControl("", [Validators.maxLength(1000)]),
			amount: new FormControl(null, [Validators.required, Validators.min(1)]),
			language: new FormControl("ar"),
		});
	}

	ngOnInit(): void {
		this.ensureMerchantsAndLoadList();
	}

	ensureMerchantsAndLoadList(): void {
		if (this.cpMerchantsLoaded) {
			this.cpMerchants = this.cpMerchants.map((m: any) => ({
				...m,
				display_label: this.getCpMerchantDisplayName(m),
			}));
			this.loadCustomPaymentsList();
			return;
		}
		this.spinner.show();
		this.service.getAllMerchantInfo().subscribe({
			next: (merchants) => {
				this.cpMerchants = (merchants || []).map((m: any) => ({
					...m,
					display_label: this.getCpMerchantDisplayName(m),
				}));
				this.cpMerchantsLoaded = true;
				this.spinner.hide();
				this.loadCustomPaymentsList();
			},
			error: (error) => {
				this.spinner.hide();
				this.showError(this.extractErrorMessage(error));
			},
		});
	}

	openCreateModal(content: any): void {
		this.customPaymentForm.reset({
			merchant_id: null,
			title: "",
			description: "",
			amount: null,
			language: this.curentLang === "he" ? "he" : this.curentLang === "en" ? "en" : "ar",
		});

		const open = (): void => {
			this.modalService.open(content, { size: "lg", centered: true, scrollable: true });
		};

		if (this.cpMerchantsLoaded) {
			this.cpMerchants = this.cpMerchants.map((m: any) => ({
				...m,
				display_label: this.getCpMerchantDisplayName(m),
			}));
			open();
			return;
		}

		this.spinner.show();
		this.service.getAllMerchantInfo().subscribe({
			next: (merchants) => {
				this.cpMerchants = (merchants || []).map((m: any) => ({
					...m,
					display_label: this.getCpMerchantDisplayName(m),
				}));
				this.cpMerchantsLoaded = true;
				this.spinner.hide();
				open();
			},
			error: (error) => {
				this.spinner.hide();
				this.showError(this.extractErrorMessage(error));
			},
		});
	}

	backToPlans(): void {
		this.router.navigate(["/dashboard/plans-info"]);
	}

	loadCustomPaymentsList(): void {
		this.spinner.show();
		this.service
			.getAdminCustomPayments({
				page: this.cpPage,
				limit: this.cpLimit,
				...(this.cpFilterMerchantId != null
					? { merchant_id: this.cpFilterMerchantId }
					: {}),
				...(this.cpFilterStatus ? { status: this.cpFilterStatus } : {}),
			})
			.subscribe({
				next: (res) => {
					this.cpRows = res?.data || [];
					this.cpTotal = res?.total ?? 0;
					this.cpPage = res?.page ?? this.cpPage;
					this.cpLimit = res?.limit ?? this.cpLimit;
					this.spinner.hide();
				},
				error: (error) => {
					this.spinner.hide();
					this.showError(this.extractErrorMessage(error));
				},
			});
	}

	applyCustomPaymentFilters(): void {
		this.cpPage = 1;
		this.loadCustomPaymentsList();
	}

	setCustomPaymentPage(nextPage: number): void {
		const totalPages = Math.max(1, Math.ceil(this.cpTotal / this.cpLimit) || 1);
		const p = Math.min(Math.max(1, nextPage), totalPages);
		if (p === this.cpPage) {
			return;
		}
		this.cpPage = p;
		this.loadCustomPaymentsList();
	}

	get cpTotalPages(): number {
		return Math.max(1, Math.ceil(this.cpTotal / this.cpLimit) || 1);
	}

	submitCustomPayment(): void {
		if (this.customPaymentForm.invalid) {
			this.customPaymentForm.markAllAsTouched();
			this.showWarning(
				this.t(
					"يرجى تعبئة الحقول المطلوبة بشكل صحيح",
					"Please fill all required fields correctly",
					"אנא מלא את כל השדות הנדרשים בצורה תקינה"
				)
			);
			return;
		}

		const v = this.customPaymentForm.value;
		const payload: any = {
			merchant_id: Number(v.merchant_id),
			title: (v.title || "").toString().trim(),
			amount: Number(v.amount),
			language: (v.language || "ar").toString(),
		};
		const desc = (v.description || "").toString().trim();
		if (desc.length) {
			payload.description = desc;
		}

		this.isCustomPaymentSubmitting = true;
		this.spinner.show();
		this.service.createAdminCustomPayment(payload).subscribe({
			next: (res) => {
				this.spinner.hide();
				this.isCustomPaymentSubmitting = false;
				const url = res?.payment_url as string | undefined;
				if (url) {
					this.lastPaymentUrl = url;
				}
				if (url) {
					Swal.fire({
						icon: "success",
						title: this.t("تم إنشاء رابط الدفع", "Payment link created", "קישור התשלום נוצר"),
						html: `<div style="word-break:break-all">${url}</div>`,
						showDenyButton: true,
						showCancelButton: true,
						confirmButtonText: this.t("فتح الرابط", "Open Link", "פתח קישור"),
						denyButtonText: this.t("نسخ الرابط", "Copy Link", "העתק קישור"),
						cancelButtonText: this.t("إغلاق", "Close", "סגור"),
					}).then((result) => {
						if (result.isConfirmed) {
							window.open(url, "_blank");
						}
						if (result.isDenied) {
							this.copyCustomPaymentUrl(url);
						}
					});
				} else {
					this.showSuccess(
						this.t("تم إنشاء طلب الدفع", "Payment request created", "בקשת התשלום נוצרה")
					);
				}
				this.customPaymentForm.patchValue({
					merchant_id: null,
					title: "",
					description: "",
					amount: null,
				});
				this.modalService.dismissAll();
				this.loadCustomPaymentsList();
			},
			error: (error) => {
				this.spinner.hide();
				this.isCustomPaymentSubmitting = false;
				this.showError(this.extractErrorMessage(error));
			},
		});
	}

	copyLastPaymentLink(): void {
		if (this.lastPaymentUrl) {
			this.copyCustomPaymentUrl(this.lastPaymentUrl);
		}
	}

	deleteCustomPayment(row: any): void {
		if (row?.status !== "pending") {
			return;
		}
		Swal.fire({
			icon: "warning",
			title: this.t(
				"حذف طلب الدفع المعلق؟",
				"Delete this pending custom payment?",
				"למחוק את בקשת התשלום הממתינה?"
			),
			showCancelButton: true,
			confirmButtonText: this.t("حذف", "Delete", "מחק"),
			cancelButtonText: this.t("إلغاء", "Cancel", "בטל"),
		}).then((result) => {
			if (!result.isConfirmed) {
				return;
			}
			this.spinner.show();
			this.service.deleteAdminCustomPayment(row.id).subscribe({
				next: (res) => {
					this.spinner.hide();
					const msg =
						this.curentLang === "ar"
							? res?.message_ar || res?.messageAr
							: this.curentLang === "he"
								? res?.message_he || res?.messageHe
								: res?.message_en || res?.messageEn;
					this.showSuccess(
						msg ||
							this.t(
								"تم حذف طلب الدفع",
								"Custom payment deleted",
								"בקשת התשלום נמחקה"
							)
					);
					this.loadCustomPaymentsList();
				},
				error: (error) => {
					this.spinner.hide();
					this.showError(this.extractErrorMessage(error));
				},
			});
		});
	}

	copyCustomPaymentUrl(url: string): void {
		if (!url) {
			return;
		}
		if (navigator?.clipboard?.writeText) {
			navigator.clipboard
				.writeText(url)
				.then(() => {
					this.showSuccess(this.t("تم نسخ الرابط", "Link copied", "הקישור הועתק"));
				})
				.catch(() => this.copyCustomPaymentUrlFallback(url));
			return;
		}
		this.copyCustomPaymentUrlFallback(url);
	}

	getCpMerchantDisplayName(merchant: any): string {
		if (!merchant) {
			return "-";
		}
		if (this.curentLang === "ar") {
			return (
				merchant.merchant_name_ar ||
				merchant.merchant_name_en ||
				merchant.merchant_name_he ||
				`#${merchant.merchant_id}`
			);
		}
		if (this.curentLang === "he") {
			return (
				merchant.merchant_name_he ||
				merchant.merchant_name_en ||
				merchant.merchant_name_ar ||
				`#${merchant.merchant_id}`
			);
		}
		return (
			merchant.merchant_name_en ||
			merchant.merchant_name_ar ||
			merchant.merchant_name_he ||
			`#${merchant.merchant_id}`
		);
	}

	getCpMerchantName(merchantId: number): string {
		const merchant = (this.cpMerchants || []).find(
			(m: any) => Number(m.merchant_id) === Number(merchantId)
		);
		if (!merchant) {
			return `#${merchantId}`;
		}
		return this.getCpMerchantDisplayName(merchant);
	}

	customPaymentStatusClass(status: string): string {
		if (status === "paid") {
			return "bg-success";
		}
		if (status === "failed") {
			return "bg-danger";
		}
		return "bg-warning text-dark";
	}

	customPaymentStatusLabel(status: string): string {
		if (status === "paid") {
			return this.t("مدفوع", "Paid", "שולם");
		}
		if (status === "failed") {
			return this.t("فشل", "Failed", "נכשל");
		}
		return this.t("معلق", "Pending", "ממתין");
	}

	isInvalidCp(controlName: string): boolean {
		const control = this.customPaymentForm.get(controlName);
		return !!control && control.invalid && (control.touched || control.dirty);
	}

	t(ar: string, en: string, he: string): string {
		if (this.curentLang === "ar") {
			return ar;
		}
		if (this.curentLang === "he") {
			return he;
		}
		return en;
	}

	private extractErrorMessage(error: any): string {
		const apiError = error?.error;
		const apiMessage = apiError?.message;

		if (this.curentLang === "ar" && (apiError?.messageAr || apiError?.message_ar)) {
			return apiError.messageAr || apiError.message_ar;
		}
		if (this.curentLang === "en" && (apiError?.messageEn || apiError?.message_en)) {
			return apiError.messageEn || apiError.message_en;
		}
		if (this.curentLang === "he" && (apiError?.messageHe || apiError?.message_he)) {
			return apiError.messageHe || apiError.message_he;
		}

		if (Array.isArray(apiMessage) && apiMessage.length) {
			return apiMessage.join(", ");
		}

		if (typeof apiMessage === "string" && apiMessage.trim().length) {
			return apiMessage;
		}

		return this.t("حدث خطأ غير متوقع", "Unexpected error occurred", "אירעה שגיאה לא צפויה");
	}

	private showSuccess(title: string): void {
		Swal.fire({ icon: "success", title });
	}

	private showError(title: string): void {
		Swal.fire({ icon: "error", title });
	}

	private showWarning(title: string): void {
		Swal.fire({ icon: "warning", title });
	}

	private copyCustomPaymentUrlFallback(link: string): void {
		const textarea = document.createElement("textarea");
		textarea.value = link;
		textarea.style.position = "fixed";
		textarea.style.opacity = "0";
		document.body.appendChild(textarea);
		textarea.select();
		document.execCommand("copy");
		document.body.removeChild(textarea);
		this.showSuccess(this.t("تم نسخ الرابط", "Link copied", "הקישור הועתק"));
	}
}
